import userApi from '@/api/user'
import storeApi from '@/api/store'
import router from '@/router'
import i18n from '@/i18n'
import { logEvent, setUserId } from '@/utils/diagnostics'

// Last-known-good cache for the branch list. Branches change rarely, so once we've
// fetched them successfully we persist them and reuse them whenever a later fetch
// fails or returns empty — the dropdown then never renders empty after the first
// successful load. Reads/writes are guarded because localStorage can throw in some
// in-app browsers / private mode.
const BRANCHES_CACHE_KEY = 'branches_cache'

// Hard-coded branch list used as a last-resort fallback when the API is unreachable
// and localStorage is empty or unavailable. Keep in sync with actual branch data.
const BRANCHES_FALLBACK = [
  { id: 3,  name: 'Bangkhunnon (บางขุนนนท์)',                           is_default: false },
  { id: 2,  name: 'Banglamphu (บางลำพู)',                               is_default: false },
  { id: 12, name: 'Central Plaza Westgate (เซ็นทรัลพลาซา เวสต์เกต)',   is_default: false },
  { id: 7,  name: 'Central Rama 9 (เซ็นทรัล พระราม 9)',                is_default: false },
  { id: 4,  name: 'CentralwOrld (เซ็นทรัลเวิลด์)',                     is_default: false },
  { id: 5,  name: 'Chaengwattana (แจ้งวัฒนะ)',                         is_default: false },
  { id: 9,  name: 'Future Park Rangsit (ฟิวเจอร์ พาร์ค รังสิต)',       is_default: false },
  { id: 8,  name: 'Mega Bangna (เมกาบางนา)',                           is_default: false },
  { id: 14, name: 'Pattaya (พัทยา)',                                    is_default: false },
  { id: 17, name: 'Phuket (ภูเก็ต)',                                    is_default: false },
  { id: 16, name: 'Suratthani (สุราษฎร์ธานี)',                          is_default: false },
  { id: 18, name: 'Terminal 21 Rama 3 (เทอร์มินอล 21 พระราม 3)',       is_default: false },
  { id: 1,  name: 'The Mall Bangkae (เดอะมอลล์ บางแค)',                is_default: false },
  { id: 11, name: 'The Mall Bangkapi (เดอะมอลล์ บางกะปิ)',             is_default: false },
  { id: 10, name: 'The Promenade (เดอะพรอมานาด)',                      is_default: false },
  { id: 15, name: 'Trang (ตรัง)',                                       is_default: false },
  { id: 13, name: 'Udonthani (อุดรธานี)',                               is_default: false },
]

// A usable branch carries the two fields the dropdown actually needs: an id (its
// value) and a non-empty name (its label). We drop malformed items individually
// rather than rejecting the whole response — a single bad row (e.g. a branch saved
// with a blank name) must not blank out the dropdown for everyone. We intentionally
// do NOT enforce a minimum length — the branch count legitimately changes
// (closures/openings), so a smaller-but-valid list must still be accepted.
const sanitizeBranchList = (data) => {
  if (!Array.isArray(data)) return []
  return data.filter(
    (b) =>
      b &&
      b.id !== undefined && b.id !== null && b.id !== '' &&
      typeof b.name === 'string' && b.name.trim() !== '',
  )
}

const readBranchesCache = () => {
  try {
    const raw = localStorage.getItem(BRANCHES_CACHE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch (error) {
    return []
  }
}

const writeBranchesCache = (list) => {
  try {
    localStorage.setItem(BRANCHES_CACHE_KEY, JSON.stringify(list))
  } catch (error) {
    // localStorage unavailable (private mode / in-app browser) — cache is best-effort.
  }
}

export default {
  namespaced: true,
  state: {
    address: null,
    addresses: [],
    data: null,
    profile: null,
    continueDrugs: [],
    howToUseMyContinueDrugs: [],
    requestIdOtp: null,
    // Seed from last-known-good cache so the dropdown is populated instantly on load,
    // then refreshed by fetchGetBranches.
    branches: readBranchesCache(),
    showVerify: false,
    provinces: [],
    districts: [],
    subDistricts: [],
    notFound: false,
  },
  getters: {
    getUser: state => state.data,
    getUserProfile: state => state.profile,
    getMyContinueDrugs: state => state.continueDrugs || [],
    getHowToUseMyContinueDrugs: state => state.howToUseMyContinueDrugs || [],
    getSelectedAddress: state => {
   
      const foundSelected = state.addresses.find(address => address.isSelected)
      if (foundSelected) return foundSelected
      const primary = state.addresses.find(address => address.is_primary)
      
      return primary
    },
    getBranchOptions: state => state.branches.map(branch => ({ ...branch, value: branch.id, label: branch.name }))
  },
  actions: {
    async fetchLoginWithLine({ commit }, { lineAccessToken, s, attachCurrentUser = false }) {
      const response = await userApi.loginWithLine(lineAccessToken, s, { attachCurrentUser })
      commit('SET_USER', response)
    },
    async fetchCheckLineLoginStatus(_, { lineAccessToken }) {
      return await userApi.checkLineLoginStatus(lineAccessToken)
    },
    async fetchLoginWithUsername({ commit }, { username, password }) {
      const response = await userApi.loginWithUsername(username, password)
      commit('SET_USER', response)
    },
    async fetchGetUserProfile({ commit, state }, redirect) {
      if (!state.data) {
        return
      }
      const response = await userApi.getProfileUser()
      const href = window.location.href
      const isOnVerify = href.includes('/verify')
      const isOnLoginWithPhone = href.includes('/login-with-phone')
      if (redirect === '/my-profile') {
        if (!response.makalin_user_id) {
          if (!isOnVerify) router.push('/user/verify')
          return
        }
        if (!response.is_verified_phone) {
          if (!isOnLoginWithPhone && !isOnVerify) router.push('/user/login-with-phone')
          return
        }
      }
      commit('SET_USER_PROFILE', response)
      if (isOnVerify) return
      const redirectTo = sessionStorage.getItem('redirectTo')
      if (redirectTo) {
        sessionStorage.removeItem('redirectTo')
        window.location.href = redirectTo
      }
      else if (redirect) {
        router.replace(redirect)
      }
    },
    async fetchUpdateMakalinPhone({ commit }, phone) {
      const response = await userApi.updateMakalinPhone(phone)
      const userProfileresponse = await userApi.getProfileUser()
      commit('SET_USER_PROFILE', userProfileresponse)

      return response
    },
    async fetchUpdateUser({ commit }, body) {
      const response = await userApi.updateUser(body)
      commit('SET_USER_PROFILE', response)
      commit('snackBar/SET_DATA', { message: i18n.global.t('successUpdateProfile'), color: 'success' }, { root: true })
    },
    async fetchUpdateAvatar({ commit }, { file, callback }) {
      const response = await userApi.updateAvatar(file)
      callback()
      commit('SET_USER_PROFILE', response)
      commit('snackBar/SET_DATA', { message: i18n.global.t('successUpdateProfile'), color: 'success' }, { root: true })
    },
    async fetchVerifyLaserOTP({ commit, state }, { otp, name, productId }) {
      const response = await userApi.verifyLaserOTP(otp, state.requestIdOtp, productId, name)
      commit('SET_REQUEST_ID_OTP', response)
    },
    async fetchGetLaserOTP({ commit }, { phone }) {
      const response = await userApi.getLaserOTP(phone)
      commit('SET_REQUEST_ID_OTP', response.request_id)
    },
    async fetchVerifyOTP({ commit, state }, otp) {
      const response = await userApi.verifyOTP(otp, state.requestIdOtp)
      commit('SET_USER', response)
    },
    async fetchCheckPhoneCustomer(_, phone) {
      return await userApi.checkPhoneCustomer(phone)
    },
    async fetchGetOTP({ commit }, { phone, branchId }) {
      const response = await userApi.getOTP(phone, branchId)
      commit('SET_REQUEST_ID_OTP', response.request_id)
      return response
    },
    async fetchRegisterPhoneCustomer({ commit }, body) {
      const response = await userApi.registerPhoneCustomer(body)
      commit('SET_REQUEST_ID_OTP', response.request_id)
      return response
    },
    async fetchVerifyUserOTP({ commit, state }, otp) {
      try {
        const response = await userApi.verifyUserOTP(otp, state.requestIdOtp)
        commit('snackBar/SET_DATA', { message: response.detail, color: 'success' }, { root: true })
        router.replace('/my-profile')
      }
      catch (err) {
        commit('snackBar/SET_DATA', { message: err.detail, color: 'error' }, { root: true })
        // Rethrow so the view can route the user out of the flow on its own terms.
        throw err
      } 
    },
    async fetchGetVerifyUserOTP({ commit }, { phone, branchId }) {
      try {
        const response = await userApi.getVerifyUserOTP(phone, branchId)
        commit('SET_REQUEST_ID_OTP', response.request_id)
      }
      catch (err) {
        if (err.status === 404) {
          commit('SET_NOT_FOUND', true)
          commit('snackBar/SET_DATA', { message: err.detail, color: 'warning' }, { root: true })
        }
        // Rethrow so the view can route the user out of the flow on its own terms.
        throw err
      }
    },
    async fetchResetPassword({ commit, state }, { newPassword, reNewPassword }) {
      await userApi.setNewPassword(newPassword, reNewPassword, state.requestIdOtp)
      commit('snackBar/SET_DATA', { message: i18n.global.t('successUpdatePassword'), color: 'success' }, { root: true })
      router.replace('/user/login')
    },
    async fetchVerifyForgetPasswordOTP({ state }, otp) {
      await userApi.verifyForgetPasswordOTP(otp, state.requestIdOtp)
      router.push('/user/forget-password/new')
    },
    async fetchGetForgetPasswordOTP({ commit }, phone) {
      try {
        const response = await userApi.getForgetPasswordOTP(phone)
        commit('SET_REQUEST_ID_OTP', response.request_id)
      }
      catch (err) {
        commit('snackBar/SET_DATA', { message: err.detail, color: 'error' }, { root: true })
      } 
    },
    async fetchMyContinueDrugs({ commit }) {
      try {
        const data = await userApi.getMyContinueDrugs()
        commit('SET_CONTINUE_DRUGS', data?.continue_drug_products || [])
      }
      catch (err) {
        commit('snackBar/SET_DATA', { message: i18n.global.t('loginFirst'), color: 'warning' }, { root: true })
        router.replace('/user/login')
      }
    },
    async fecthHowToUseMyContinueDrugs({ commit }) {
      try {
        const data = await userApi.getHowToUseMyContinueDrugs()
        commit('SET_HOW_TO_USE_CONTINUE_DRUGS', data || [])
      }
      catch (err) {
        sessionStorage.setItem('redirectTo', window.location.href)
        commit('snackBar/SET_DATA', { message: i18n.global.t('loginFirst'), color: 'warning' }, { root: true })
        router.replace('/user/login')
      }
    },
    async fetchGetAddress({ commit }, id) {
      const data = await userApi.getAddress(id)
      commit('SET_ADDRESS', data)
    },
    async fetchGetAddresses({ commit }) {
      try{
        const data = await userApi.getAddresses()
        console.log(data)
        commit('SET_ADDRESSES', data)
      }catch(err){
        let data =  localStorage.getItem('userAddress')
   
        if(Array.isArray(JSON.parse(data))){
          commit('SET_ADDRESSES',  JSON.parse(data))
        }
       
      }
     
    },
    async fetchAddNewAddress({ commit }, body) {
      try {
        await userApi.addNewAddress(body)
        commit('snackBar/SET_DATA', { message: i18n.global.t('myAddresses.successAdd'), color: 'success' }, { root: true })
        router.replace('/my-addresses')
      }
      catch (err) {
        commit(
          'snackBar/SET_DATA',
          { message: err.detail || i18n.global.t('somethingWrong'), color: 'error' },
          { root: true }
        )
        throw err
      }
    },
    async fetchUpdateAddress({ commit }, { id, body }) {
      await userApi.updateAddress(id, body)
      commit('snackBar/SET_DATA', { message: i18n.global.t('myAddresses.successUpdate'), color: 'success' }, { root: true })
      router.replace('/my-addresses')
    },
    async fetchDeleteAddress({ commit }, id) {
      await userApi.deleteAddress(id)
      const data = await userApi.getAddresses()
      commit('snackBar/SET_DATA', { message: i18n.global.t('myAddresses.successDelete'), color: 'success' }, { root: true })
      commit('SET_ADDRESSES', data)
    },
    async fetchGetBranches({ commit, state }) {
      // Diagnostics for the empty-branch-dropdown bug: log every branch-fetch outcome
      // with rich client context (build id, LINE/LIFF version, device, network, cache,
      // timing). Failures are always logged; a successful load is sampled once per
      // session to keep the log table light. See @/utils/diagnostics.
      // Query later: event="branch_fetch" in the `logs` table.
      const hadToken = !!(state.data && state.data.access)
      const userId = (state.profile && state.profile.id) || 0
      const startedAt = Date.now()
      const logBranchFetch = (info, opts) =>
        logEvent('branch_fetch', { ...info, hadToken, durationMs: Date.now() - startedAt }, { userId, ...(opts || {}) })

      // Fetch fresh; on a good response, update the last-known-good cache. If the
      // fetch fails or comes back empty, fall back to the cache so the dropdown is
      // never empty after the first successful load. Only show the "tap to retry"
      // error state when there is genuinely nothing to show (no fresh data, no cache).
      try {
        const data = await userApi.getBranches()
        const usable = sanitizeBranchList(data)
        const received = Array.isArray(data) ? data.length : 0
        if (usable.length) {
          commit('SET_BRANCHES', usable)
          writeBranchesCache(usable)
          // `dropped` surfaces silently-skipped rows so a bad branch record shows up
          // in the logs instead of quietly shrinking the list. Sampled: one OK per
          // session (failures below are never sampled).
          logBranchFetch({ outcome: 'ok', count: usable.length, dropped: received - usable.length }, { once: 'branch_fetch_ok' })
          return
        }
        // Nothing usable in the response -> fall back to last-known-good, then hardcoded.
        const cached = readBranchesCache()
        const fallback = cached.length ? cached : BRANCHES_FALLBACK
        commit('SET_BRANCHES', fallback)
        logBranchFetch({
          outcome: 'empty_or_invalid',
          received: Array.isArray(data) ? data.length : typeof data,
          usedCache: cached.length > 0,
          cacheCount: cached.length,
          usedFallback: cached.length === 0,
        })
      } catch (error) {
        // Network/5xx -> fall back to last-known-good, then hardcoded; no error surfaced.
        const cached = readBranchesCache()
        const fallback = cached.length ? cached : BRANCHES_FALLBACK
        commit('SET_BRANCHES', fallback)
        logBranchFetch({
          outcome: 'error',
          status: (error && error.status) || null,
          detail: (error && (error.detail || error.message)) || String(error),
          usedCache: cached.length > 0,
          cacheCount: cached.length,
          usedFallback: cached.length === 0,
        })
      }
    },
    async fetchGetTermAndPrivacy({ commit }) {
      const data = await userApi.getTermAndPrivacy()
      commit('SET_TERM_DESC', data)
    },
    async fetchAcceptPrivacy({ commit }) {
      await userApi.acceptPrivacy()
      commit('SET_TERM_DESC', null)
    },
    async fetchGetProvince({ commit }) {
      commit('SET_DISTRICTS', [])
      commit('SET_SUB_DISTRICTS', [])
      const items = await userApi.getProvince()
      commit('SET_PROVINCES', items)
    },
    async fetchGetDistrict({ commit }, provinceCode) {
      commit('SET_SUB_DISTRICTS', [])
      if (provinceCode) {
        const items = await userApi.getDistrict(provinceCode)
        commit('SET_DISTRICTS', items)
      }
      else {
        commit('SET_DISTRICTS', [])
      }
    },
    async fetchGetSubDistrict({ commit }, districtCode) {
      if (districtCode) {
        const items = await userApi.getSubDistrict(districtCode)
        commit('SET_SUB_DISTRICTS', items)
      }
      else {
        commit('SET_SUB_DISTRICTS', [])
      }
    },
    async fecthUpdateProductStock(context, data) {
      await storeApi.updateProductStock(data)
    },
  },
  mutations: {
    SET_SHOW_VERIFY(state, data) {
      state.showVerify = data
    },
    RESET_REQUEST_ID_OTP(state) {
      state.requestIdOtp = null
    },
    SET_REQUEST_ID_OTP(state, data) {
      state.requestIdOtp = data
    },
    SET_USER(state, data) {
      state.data = { ...state.data, ...data }
    },
    RESET_USER(state) {
      state.data = null
      state.profile = null
    },
    SET_USER_PROFILE(state, data) {
      state.profile = data
      setUserId(data && data.id)
    },
    SET_NOT_FOUND(state, data) {
      state.notFound = data
    },
    SET_CONTINUE_DRUGS(state, data) {
      state.continueDrugs = data
    },
    SET_HOW_TO_USE_CONTINUE_DRUGS(state, data) {
      state.howToUseMyContinueDrugs = data
    },
    SET_ADDRESSES(state, data) {
      const foundSelected = state.addresses.find(address => address.isSelected)
      if (foundSelected) {
        state.addresses = data.map(address => {
          if (address.id === foundSelected.id) {
            return {
              ...address,
              isSelected: true,
            }
          }
          return address
        })
      }
      else {
        state.addresses = data
      }
    },
    SET_ADDRESS(state, data) {
      state.address = data
    },
    RESET_ADDRESS(state) {
      state.address = null
    },
    SET_BRANCHES(state, data) {
      state.branches = data
    },
    SET_TERM_DESC(state, data) {
      state.termDescription = data
    },
    SET_PROVINCES(state, data) {
      state.provinces = data
    },
    SET_DISTRICTS(state, data) {
      state.districts = data
    },
    SET_SUB_DISTRICTS(state, data) {
      state.subDistricts = data
    },
    SET_SELCETED_ADDRESS(state, id) {
      state.addresses = state.addresses.map(address => {
        if (id === address.id) {
          return {
            ...address,
            isSelected: true,
          }
        }
        return {
          ...address,
          isSelected: false,
        }
      })
    }
  },
}
